import { reactive } from 'vue'

import {
  MicrofrontendEvents,
  onDispatchToastNotification,
  publish,
} from '@sennder/senn-node-microfrontend-event-bus'
import {
  GetTokenOptions,
  IAction,
  IFeatureFlags,
  IMicrofrontendData,
  IOrg,
  ISennTmsSharedData,
  IUser,
  IUserRole,
  OpsProfile,
  OrgType,
} from '@sennder/senn-node-microfrontend-interfaces'

import { logger } from '@/services/logger/loggers'
import { logout } from '@/store/logoutActions'
import errorsHandler from '@/services/errors-handler'
import { analytics } from '@/services/analyticsProvider'
import { i18n, defaultLanguage } from '@/services/i18n'
import {
  TENANT,
  SHARED_DATA_TYPE,
  ORCAS_URL,
  OPERATIONS_URL,
  SHOW_LANGUAGE_PICKER,
} from '@/common/config'
import { userManagementService } from '@/services/userManagementService'
import { sendErrorInMonitor } from '@/services/monitor'
import { auth } from '@/modules/auth'
import { notify } from './notify'
import { canAccessCharteringPages } from '@/config/user-access'
import startAsyncService from '@/services/retry-service'
import { Prettify, Optional } from '@/types/types'
import { featureFlag } from '@/services/featureFlagProvider'

// Constants for audiences
const DEFAULT_AUDIENCE = 'https://api.cloud.sennder.com'

// Default options for getting an auth token
const defaultTokenOptions: GetTokenOptions = {
  usePopup: false,
  throwException: false,
}

const getAuthToken = async (options: GetTokenOptions = defaultTokenOptions) => {
  if (!auth.value) {
    throw new Error('[tms-shell - getAuthToken]: Auth module is not loaded')
  }

  const audience = options.audience || DEFAULT_AUDIENCE
  const enrichedOptions = { ...options, audience }

  const token = (await auth.value.getAuthToken(enrichedOptions)) || null

  return token
}

const getAuthHeader = async (
  options: GetTokenOptions = defaultTokenOptions
) => {
  const token = await getAuthToken(options)
  return token ? `Bearer ${token}` : null
}

async function getCommonHeaders() {
  if (!auth.value) {
    throw new Error('[tms-shell - getCommonHeaders]: Auth module is not loaded')
  }
  //NOTE: Currently we are using the same audience for all the requests
  return await auth.value.getCommonHeaders(DEFAULT_AUDIENCE)
}

async function getPermissions(actions: IAction[]) {
  if (!auth.value) {
    throw new Error('[tms-shell - getPermissions]: Auth module is not loaded')
  }
  return await auth.value.getPermissions(actions, DEFAULT_AUDIENCE)
}

export async function getOrgTypeAndOrgIdOrFail() {
  if (!auth.value) {
    throw new Error(
      '[tms-shell - getOrgTypeAndOrgIdOrFail]: Auth module is not loaded'
    )
  }
  return await auth.value.getOrgTypeAndOrgIdOrFail()
}
export type SennTmsStoreData = Prettify<
  Optional<ISennTmsSharedData<object>, 'profile'>
> & {
  org?: Org
  user?: IUser
  roles: IUserRole[]
}

type Org = {
  orgID: string
  orgType: OrgType
}

const getEmptyData = (): SennTmsStoreData => {
  return {
    language: defaultLanguage,
    tenant: TENANT,
    type: SHARED_DATA_TYPE,
    featureFlags: {},
    /**
     * TODO: some Octopus micro-frontends expect motership and orcas urls to be passed by the shell because of the multi-tenancy.
     * We can get rid of this and define all the URLs in the micro-frontends themselves.
     */
    env: {
      OPERATIONS_BACKEND_URL: OPERATIONS_URL as string,
      ORCAS_URL: ORCAS_URL as string,
    },
    roles: [],
  }
}

export const store = reactive({
  state: {
    data: getEmptyData(),
    callbacks: {
      getToken: getAuthHeader,
      getAuthToken,
      getAuthHeader,
      getCommonHeaders,
      // @ts-expect-error Untyped in MF Interfaces, only used by internal-auth-mf-v2
      getOrgTypeAndOrgIdOrFail,
      getPermissions,
      syncParentRouter: () => {},
      segmentTrackEvent: () => {},
      onUnauthorized: async () => {
        return logout()
      },
      onUnhandledError: errorsHandler,
    },
    providers: {
      featureFlag: {
        getFeatureFlag: featureFlag.getFeatureFlag,
        watchFeatureFlag: featureFlag.watchFeatureFlag,
        trackEvent: featureFlag.trackEvent,
      },
      monitoring: {
        sendError: sendErrorInMonitor,
      },
      logger,
      notifications: {
        error: (message: string) => notify(message, 'error'),
        success: (message: string) => notify(message, 'success'),
      },
      analytics,
    },
  } satisfies IMicrofrontendData<SennTmsStoreData>,
})

export const getStateData = (): SennTmsStoreData => {
  return store.state.data
}

// Loosely typed and dangerous, only use this function from dev-panel
export const DEV_setStateData = (data: object) => {
  // @ts-expect-error
  store.state.data = data
}

export const getUserProfile = () => {
  if (!store.state.data.profile) {
    throw new Error(
      '[tms-shell - getUserProfile] state.data.profile is not initialized'
    )
  }

  return store.state.data.profile
}

export const getStateProviders = () => {
  return store.state.providers
}

export const getStateCallbacks = () => {
  return store.state.callbacks
}

export const loadState = async () => {
  let user: IUser
  let org: Org | undefined
  let opsProfile: OpsProfile
  try {
    ;[org, { opsProfile, user }] = await Promise.all([
      fetchOrg(),
      userManagementService.getUserAndProfile(),
    ])
    setStoreLanguage(SHOW_LANGUAGE_PICKER ? user.language : 'en')
  } catch (error) {
    logger.error(
      '[tms-shell - userManagementService]: User data can not be loaded',
      {
        error,
      }
    )

    publish<onDispatchToastNotification>({
      name: MicrofrontendEvents.onDispatchToastNotification,
      data: {
        message: i18n.global.t('toast.error.message'),
        category: 'error',
        title: i18n.global.t('toast.error.title'),
      },
    })
    await logout()
    return false
  }

  const [userRoles] = await Promise.all([
    fetchUserRoles(opsProfile.id, org),
    startAsyncService(
      'LaunchDarkly',
      () =>
        featureFlag.identify(opsProfile.id, {
          email: opsProfile.email,
          name: opsProfile.name,
        }),
      {},
      0
    ),
  ])

  store.state.data = {
    ...store.state.data,
    user,
    org,
    profile: opsProfile,
    roles: userRoles || [],
  }
  return true
}

const fetchUserRoles = async (id: string, org?: IOrg) => {
  if (!id) {
    return
  }

  try {
    const uMRoles = await userManagementService.getUserRoles(id)
    const userRoles = [...uMRoles]

    if (canAccessCharteringPages(org)) {
      if (!auth.value) {
        logger.error(
          '[tms-shell - set user chartering roles]: Auth module is not loaded',
          {}
        )
      } else {
        const charteringRole = await auth.value.getCharteringUserRole?.(id)

        if (charteringRole) {
          const baseRole: IUserRole = {
            id: charteringRole.userRoleInfo?.uuid,
            name: charteringRole.userRoleInfo?.role,
            description: charteringRole.userRoleInfo?.name,
          }

          if (baseRole.name) {
            userRoles.push(baseRole)
          }

          if (charteringRole.charteringOfficeName) {
            userRoles.push({
              ...baseRole,
              name: charteringRole.charteringOfficeName,
            })
          }
        }
      }
    }

    return userRoles
  } catch (error) {
    logger.error(
      '[tms-shell - userManagementService]: User roles can not be load',
      { error }
    )
  }
}

export const fetchOrg = async (): Promise<Org | undefined> => {
  try {
    const { orgId, orgType } = await getOrgTypeAndOrgIdOrFail()
    if (orgType && orgId) {
      return { orgID: orgId, orgType: orgType as OrgType }
    }
  } catch (error) {
    logger.error('[tms-shell - fetchOrg]: Error while getting org', {
      error,
    })
  }
}

export const setStoreLanguage = (language: string) => {
  store.state.data.language = language
}

/**
 * Updates the shell's store with new feature flags.
 * When all micro-frontends are migrated to use the new feature flag provider, this function and the related legacy flags in the store can be removed. A new, not shared state can be introduced to store flags for the shell logic.
 * @deprecated Currently is used only to maintain backwards compatibility with the old feature flags setup. Prefer using the featureFlag provider for new implementations.
 */
export const setStoreLegacyFlags = (flags: IFeatureFlags) => {
  store.state.data.featureFlags = {
    ...store.state.data.featureFlags,
    ...flags,
  }
}

/**
 * Returns the feature flags from the shell's store.
 * These flags are intended to be used only by micro-frontends that haven't migrated yet to the new feature flag provider and still rely on the old setup where the shell passes the flags down through the shared state, or for the internal shell's usage of feature flags.
 * When all micro-frontends are migrated to use the new feature flag provider, this function and the related legacy flags in the store can be removed. A new, not shared state can be introduced to store flags for the shell logic.
 * @deprecated Currently is used only to maintain backwards compatibility with the old feature flags setup. Prefer using the featureFlag provider for new implementations.
 */
export const getStoreLegacyFlags = () => {
  return store.state.data.featureFlags
}

export const setStoreData = (data: SennTmsStoreData) => {
  store.state.data = data
}

export const clearState = () => {
  store.state.data = getEmptyData()
}
